import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as api } from '@mesa-labs/mesa-api';

import {
  addColumnFilterValue,
  DateRange,
  EmptyColumnFilters,
  EnablementV2BucketFocus,
  EnablementV2ColumnFilters,
  EnablementV2FilterSet,
  EnablementV2OptionFilterKey,
  EnablementV2RangeFilterKey,
  EnablementV2ViewScope,
  normalizeFilterSet,
  NumberRange,
  removeColumnFilterValue,
  withoutScopeConflicts,
} from '../../utils/enablementV2Filters';

export type EnablementV2State = {
  scope?: EnablementV2ViewScope;
  // Optional because state persisted before MESA-4909 rehydrates without it.
  columnFilters?: EnablementV2ColumnFilters;
  /** @deprecated Pre-MESA-4909 bucket; folded into scope + columnFilters by selectEnablementV2Filters. */
  statusFilter?: api.EnablementFilter;
};

const initialState: EnablementV2State = {
  scope: api.EnablementV2Scope.InEnablement,
  columnFilters: EmptyColumnFilters,
};

/**
 * Reads the current scope + column filters, migrating state persisted before scope/queues existed.
 * Search isn't held here — the vendor table's global search owns it.
 */
export const selectEnablementV2Filters = (state: EnablementV2State): EnablementV2FilterSet => normalizeFilterSet({
  scope: state.scope,
  status: state.statusFilter,
  columnFilters: state.columnFilters,
});

// Every reducer writes the normalized shape, which also retires a legacy statusFilter and drops
// filters that contradict the scope (e.g. queues under Partner Enablement).
const writeFilterSet = (state: EnablementV2State, filters: EnablementV2FilterSet): void => {
  const { scope, columnFilters } = withoutScopeConflicts(filters);
  state.scope = scope;
  state.columnFilters = columnFilters;
  state.statusFilter = undefined;
};

type ColumnFilterValuePayload = {
  readonly key: EnablementV2OptionFilterKey;
  readonly value: string;
};

type RangeFilterPayload = {
  readonly key: EnablementV2RangeFilterKey;
  readonly range?: NumberRange;
};

type FocusPartnerPayload = {
  readonly partnerId: api.Partners;
  /** The clicked bucket, if any; applied alongside the partner. */
  readonly focus?: EnablementV2BucketFocus;
};

const enablementV2Slice = createSlice({
  name: 'enablementV2',
  initialState,
  reducers: {
    updateScope(state: EnablementV2State, action: PayloadAction<EnablementV2ViewScope>) {
      writeFilterSet(state, { ...selectEnablementV2Filters(state), scope: action.payload });
    },
    addColumnFilter(state: EnablementV2State, action: PayloadAction<ColumnFilterValuePayload>) {
      const current = selectEnablementV2Filters(state);
      writeFilterSet(state, {
        ...current,
        columnFilters: addColumnFilterValue(current.columnFilters, action.payload.key, action.payload.value),
      });
    },
    removeColumnFilter(state: EnablementV2State, action: PayloadAction<ColumnFilterValuePayload>) {
      const current = selectEnablementV2Filters(state);
      writeFilterSet(state, {
        ...current,
        columnFilters: removeColumnFilterValue(current.columnFilters, action.payload.key, action.payload.value),
      });
    },
    updateVendorActionItemsFilter(state: EnablementV2State, action: PayloadAction<api.VendorActionItemQueueFilter | undefined>) {
      const current = selectEnablementV2Filters(state);
      writeFilterSet(state, { ...current, columnFilters: { ...current.columnFilters, vendorActionItems: action.payload } });
    },
    updateHistoricalInvoicesImportedFilter(state: EnablementV2State, action: PayloadAction<boolean | undefined>) {
      const current = selectEnablementV2Filters(state);
      writeFilterSet(state, { ...current, columnFilters: { ...current.columnFilters, historicalInvoicesImported: action.payload } });
    },
    updateRangeFilter(state: EnablementV2State, action: PayloadAction<RangeFilterPayload>) {
      const current = selectEnablementV2Filters(state);
      writeFilterSet(state, {
        ...current,
        columnFilters: { ...current.columnFilters, [action.payload.key]: action.payload.range },
      });
    },
    updateCreatedAtFilter(state: EnablementV2State, action: PayloadAction<DateRange | undefined>) {
      const current = selectEnablementV2Filters(state);
      writeFilterSet(state, { ...current, columnFilters: { ...current.columnFilters, createdAt: action.payload } });
    },
    // Partner summary cell click: resets every filter to just that partner (plus the clicked bucket,
    // for a bucket column) so the table shows exactly what the summary cell counted.
    focusPartner(state: EnablementV2State, action: PayloadAction<FocusPartnerPayload>) {
      const { partnerId, focus } = action.payload;
      writeFilterSet(state, {
        scope: focus?.scope ?? api.EnablementV2Scope.InEnablement,
        columnFilters: { ...EmptyColumnFilters, ...focus?.columnFilters, partners: [partnerId] },
      });
    },
    clearColumnFilters(state: EnablementV2State) {
      writeFilterSet(state, { ...selectEnablementV2Filters(state), columnFilters: EmptyColumnFilters });
    },
    applyFilterSet(state: EnablementV2State, action: PayloadAction<EnablementV2FilterSet>) {
      writeFilterSet(state, action.payload);
    },
  },
});

export const {
  updateScope,
  addColumnFilter,
  removeColumnFilter,
  updateRangeFilter,
  updateCreatedAtFilter,
  updateVendorActionItemsFilter,
  updateHistoricalInvoicesImportedFilter,
  focusPartner,
  clearColumnFilters,
  applyFilterSet,
} = enablementV2Slice.actions;
export default enablementV2Slice.reducer;
